import { useView } from "@/shared/domain/views/hooks";
import {
  ConfirmationAlert,
  ConfirmationCard,
  ConfirmationField,
  ConfirmationFields,
  ConfirmationNote,
  ConfirmationPanel,
} from "@/shared/foundation/primitives";
import { useViewingContext } from "@/shared/services/ViewingContextService";

import type { ConfirmationContentProps } from "./ClientTools";
import { coerceApplyViewInput, type ApplyViewParams } from "./viewToolInput";

const PREVIEW_LABEL = "Open View preview";

/**
 * The View named by ID, in the words the user knows it by: its name and
 * table once the record lands, the bare ID until then and if it never does.
 * Approve stays live either way; a lookup that failed here is the same one
 * `execute` reports properly.
 */
function ViewById({ viewId }: { viewId: string }) {
  const view = useView(viewId);
  return (
    <ConfirmationFields>
      <ConfirmationField label="View">
        {view.data?.name ?? viewId}
      </ConfirmationField>
      {view.data !== undefined && (
        <ConfirmationField label="Table">{view.data.target}</ConfirmationField>
      )}
    </ConfirmationFields>
  );
}

/**
 * Read-only preview of a `client_apply_view` proposal.
 *
 * Opening a View changes nothing but what is on screen, so the card answers
 * one question: which View is about to be shown, plus, when asked, that it
 * will become the default for its table. A name lookup shows the name
 * and the table it will be searched on, not the match: the listing is a
 * request the card must not make, and the tool refuses an ambiguous name
 * rather than guessing.
 */
export function ApplyViewConfirmation({
  params,
}: ConfirmationContentProps<ApplyViewParams>) {
  const { resourceTable } = useViewingContext();
  const verdict = coerceApplyViewInput(params, resourceTable);

  if (!verdict.ok) {
    return (
      <ConfirmationPanel label={PREVIEW_LABEL}>
        <ConfirmationAlert>
          This request won&rsquo;t succeed. {verdict.reason}
        </ConfirmationAlert>
      </ConfirmationPanel>
    );
  }

  const lookup = verdict.payload;

  return (
    <ConfirmationPanel label={PREVIEW_LABEL}>
      <ConfirmationCard title="Open View">
        {lookup.by === "id" ? (
          <ViewById viewId={lookup.viewId} />
        ) : (
          <ConfirmationFields>
            <ConfirmationField label="View">{lookup.name}</ConfirmationField>
            <ConfirmationField label="Table">{lookup.target}</ConfirmationField>
          </ConfirmationFields>
        )}
        {lookup.setAsDefault && (
          <ConfirmationNote>
            Also becomes your default: this table will open showing it from now
            on. Only you are affected, and the picker can undo it.
          </ConfirmationNote>
        )}
      </ConfirmationCard>
    </ConfirmationPanel>
  );
}
